import { localStorageHandler } from 'core/services';
import { action, observable, makeObservable, runInAction, computed } from 'mobx';
import _ from 'lodash';

class BaasicDropdownViewStore {
	@observable value = null;
	@observable isLoading = false;
	@observable options = [];
	staticOptions = [];
	isRequired = false;
	initCallback = null;
	onChangeCallback = null;
	onBeforeChangeCallback = null;
	reference = null;
	onBlur = null;
	field = null;
	store = null;
	fetchFunc = null;
	lookupRoute = null;
	localStorageHandlerKey = null;
	error = null;
	isMulti = false;
	itemTemplate = {
		value: null,
		label: null,
	};
	filter = {
		page: 1,
		rpp: 25,
		orderDirection: 'asc',
	};
	fetchAll = false;
	headerOption = null;

	constructor(props) {
		makeObservable(this);
		if (props) {
			(async () => {
				await this.initialize(props);
			})();
		}
	}

	@action.bound
	initialize = async ({
		field,
		localStorageHandlerKey,
		store,
		lookupRoute,
		filterProperties,
		onChangeCallback,
		onBeforeChangeCallback,
		filterCallback,
		onBlur,
		staticOptions,
		isMulti = false,
		value,
		initCallback,
		isRequired,
		fetchInitialValues = true,
		fetchFunc = 'find',
		itemTemplate = {
			value: 'id',
			label: 'name',
		},
		fetchAll,
		headerOption,
		defaultInitialValue,
	}) => {
		Object.assign(this, {
			field,
			localStorageHandlerKey,
			store,
			lookupRoute,
			onChangeCallback,
			onBeforeChangeCallback,
			filterCallback,
			onBlur,
			isMulti,
			staticOptions,
			itemTemplate,
			isRequired,
			initCallback,
			fetchFunc,
			fetchAll,
			headerOption,
			defaultInitialValue,
		});
		try {
			this.isLoading = true;
			this.setValue(value, true);
			this.filter.orderBy = itemTemplate.label;
			this.filter.searchFields = [itemTemplate.label];
			if (filterProperties) {
				Object.assign(this.filter, filterProperties);
			}
			if (staticOptions) {
				this.options = _.sortBy(staticOptions, this.filter.orderBy);
			} else if (fetchInitialValues) {
				this.options = await this.fetchOptions();
			}
			if (this.localStorageHandlerKey) {
				this.setSelectedValue();
			}
			if (this.initCallback) {
				await this.initCallback(this);
			}
			if (this.defaultInitialValue && this.defaultInitialValue.value) {
				if (!this.value) {
					this.setValue(
						this.options.find(o => _.get(o, this.defaultInitialValue.path) === this.defaultInitialValue.value)
					);
				}
			}
		} catch (error) {
		} finally {
			this.isLoading = false;
		}
	};

	@computed get optionsLength() {
		return this.headerOption ? this.options.length - 1 : this.options.length;
	}

	@action.bound
	setSelectedValue = () => {
		const savedValue = localStorageHandler.get(this.localStorageHandlerKey);
		if (savedValue != null) {
			this.setValue(savedValue, true);
		}
	};

	@action.bound
	reload = async () => {
		this.isLoading = true;
		this.options = await this.fetchOptions();
		this.isLoading = false;
	};

	@action.bound
	fetchOptions = async () => {
		if (this.staticOptions == null) {
			try {
				if (this.lookupRoute != null) {
					await this.store.find(this.filter, this.lookupRoute);
				} else {
					await this.store[this.fetchFunc](this.filter);
				}
				return !this.headerOption ? this.mapResponseToOptions() : [this.headerOption, ...this.mapResponseToOptions()];
			} catch (err) {
				this.error = err;
				return [];
			}
		}
	};

	@action.bound
	onChange = async selectedValue => {
		this.setValue(selectedValue);
		if (this.staticOptions == null) {
			this.resetFilter();
			if (!this.fetchAll && this.optionsLength > this.filter.rpp) {
				this.options = await this.fetchOptions();
			}
		}
	};

	@action.bound
	onMenuClose = async () => {
		if (this.filter.search != null) {
			this.isLoading = true;
			this.resetFilter();
			this.options = await this.fetchOptions();
			this.isLoading = false;
		}
	};

	@action.bound
	setValue = async (newValue, isInitializing = false) => {
		if (this.onBeforeChangeCallback && newValue && !isInitializing) {
			await this.onBeforeChangeCallback(newValue);
		}
		this.value = this.getOption(newValue);
		if (this.localStorageHandlerKey) {
			if (newValue) {
				localStorageHandler.set(this.localStorageHandlerKey, newValue);
			} else if (!isInitializing) {
				localStorageHandler.remove(this.localStorageHandlerKey);
			}
		}
		if (this.field) {
			if (this.value == null) {
				if (!isInitializing) this.field.set('value', null);
			} else if (!this.isMulti) {
				this.field.set('value', this.value.value);
			} else {
				this.field.set(
					'value',
					_.map(this.value, selected => {
						return selected.value;
					})
				);
			}
		}
		if (this.onChangeCallback && !isInitializing) {
			await this.onChangeCallback(this.value, this.field);
		}
	};

	@action.bound
	clear = () => {
		if (this.value) this.setValue(null);
	};

	onInputChange = _.debounce((searchPhrase, { action }) => {
		if (action === 'input-change') {
			runInAction(async () => {
				if (this.staticOptions == null) {
					this.isLoading = true;
					this.resetFilter();
					this.filter.search = searchPhrase;
					this.options = await this.fetchOptions();
					this.isLoading = false;
				}
			});
		}
	}, 1000);

	@action.bound
	onMenuScrollToBottom = async () => {
		if (!this.fetchAll && this.optionsLength >= this.filter.rpp && this.staticOptions == null) {
			this.isLoading = true;
			this.filter.page += 1;
			this.options = this.options.concat(await this.fetchOptions());
			this.isLoading = false;
		}
	};

	@action.bound
	resetFilter = () => {
		Object.assign(this.filter, { page: 1, rpp: 25, search: null });
	};

	@action.bound
	updateFilter = async properties => {
		this.resetFilter();
		Object.assign(this.filter, properties);
		await this.reload();
	};

	getOption = objectValue => {
		if (objectValue) {
			if (this.isMulti) {
				const values = [];
				_.each(objectValue, item => {
					values.push(this.constructOption(item));
				});
				return values;
			} else {
				return this.constructOption(objectValue);
			}
		}
		return null;
	};

	constructOption = item => {
		if (item.hasOwnProperty('label') && item.hasOwnProperty('value')) {
			return item;
		} else {
			return {
				value: _.get(item, this.itemTemplate.value),
				label: _.get(item, this.itemTemplate.label),
				extra: item,
			};
		}
	};

	mapResponseToOptions = () => {
		if (this.filterCallback) {
			return this.filterCallback(_.has(this.store.response, 'item') ? this.store.response.item : this.store.response);
		} else {
			const options = _.map(
				_.has(this.store.response, 'item') ? this.store.response.item : this.store.response,
				item => {
					return this.constructOption(item);
				}
			);
			return options;
		}
	};
}

export default BaasicDropdownViewStore;
